import { FormattedMessage } from 'react-intl';

import { intl } from '@trello/i18n';
import { ExternalLinkIcon } from '@trello/nachos/icons/external-link';

import { useEmbeddedBoardFooterQuery } from './EmbeddedBoardFooterQuery.generated';

import styles from './EmbeddedBoardFooter.less';

interface BoardFooterProps {
  /**
   * The ID of the current board being viewed. Should be set to `null` when viewing a card on a board.
   */
  boardId: string | null;
}

/**
 * A footer that is shown at the bottom of boards when Trello is embedded in non-Atlassian products.
 */
export function EmbeddedBoardFooter({ boardId }: BoardFooterProps) {
  const { data } = useEmbeddedBoardFooterQuery({
    variables: { boardId: boardId! },
    waitOn: ['CurrentBoardInfo'],
    skip: !boardId,
  });

  const board = data?.board;

  return (
    <div className={styles.boardFooter}>
      <a
        className={styles.boardFooterLogo}
        href="/"
        aria-label={intl.formatMessage({
          id: 'templates.footer_chrome.trello-home',
          defaultMessage: 'Trello home',
          description:
            'A Trello logo shown in a footer when a Trello board is embedded in another product. The logo links to the Trello homepage.',
        })}
      />
      <div className={styles.boardFooterLinks}>
        <a href={board ? board.url : '/'} target="_blank" rel="noopener">
          <FormattedMessage
            id="templates.footer_chrome.open-in-new-window"
            defaultMessage="Open in new window"
            description="A link shown in a footer when a Trello board is embedded in another product. The link opens the board in a new window."
          />
          <ExternalLinkIcon size="small" />
        </a>
      </div>
    </div>
  );
}
